import React from 'react';
import { Button, Col, Row, Panel } from 'react-bootstrap';
import * as faIcon from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AscComponent from '../../components/AscComponent'
import AscModal from "../../containers/Elements/Modal/Modal";
import ExternalNumberModal from '../../containers/ExternalNumber/ExternalNumberModal';
import { CSVLink } from "react-csv";
import moment from "moment";
import * as GlobalConst from "../../components/AscConstants";

/**
 * 番号設定構成図コンポーネント（営業時間管理用）
 * 営業カレンダーに紐づく外線番号を2カラムテーブルで表示する
 */
class BusinessCalendarNumberSettingInfoDiagram extends AscComponent {
    constructor(props) {
        super(props);
        this.state = {
            showExternalNumberModal: false,
            externalNumberModalLoading: false,
            externalNumberModalData: null,
            csvData: [],
            csvHeader: [],
            csvLoading: false
        };
        this.csvLink = React.createRef();
    }

    componentDidMount = () => {
        this.scheduleDrawConnections();
    }

    componentDidUpdate = (prevProps) => {
        const { state } = this.props;
        const prevState = prevProps.state;

        if (state.showResourceMap && (
            (!prevState.showResourceMap) ||
            (state.resourceMapData !== prevState.resourceMapData) ||
            (prevState.resourceMapLoading && !state.resourceMapLoading)
        )) {
            this.scheduleDrawConnections();
        }
    }

    scheduleDrawConnections = () => {
        const { state } = this.props;
        const DRAW_CONNECTION_DELAY = 500;

        if (state.showResourceMap &&
            !state.resourceMapLoading &&
            state.resourceMapData) {
            setTimeout(() => {
                this.drawConnections();
            }, DRAW_CONNECTION_DELAY);
        }
    }

    drawConnections = () => {
        try {
            const { connectionLineClass } = this.props;
            const svg = document.querySelector(`.${connectionLineClass}`);
            const DRAW_CONNECTION_DELAY = 1000;
            if (!svg) {
                setTimeout(() => {
                    const retrySvg = document.querySelector(`.${connectionLineClass}`);
                    if (retrySvg) {
                        this.drawConnectionsInternal(retrySvg);
                    }
                }, DRAW_CONNECTION_DELAY);
                return;
            }
            this.drawConnectionsInternal(svg);
        } catch (error) {
            console.error(this.props.langText.Message.BusinessCalendarNumberSettingInfoDiagram.drawConnectionError, error);
        }
    };

    drawConnectionsInternal = (svg) => {
        try {
            while (svg.firstChild) {
                svg.removeChild(svg.firstChild);
            }

            const { tableClass } = this.props;
            const table = document.querySelector(`.${tableClass}`);
            if (!table) return;

            const svgRect = svg.getBoundingClientRect();
            this.drawConnectionLines(svg, svgRect, table);
        } catch (error) {
            console.error(this.props.langText.Message.BusinessCalendarNumberSettingInfoDiagram.drawConnectionError, error);
        }
    };

    /**
     * 営業カレンダーボックス→各外線番号ボックスへのL字線描画（1段のみ）
     */
    drawConnectionLines = (svg, svgRect, table) => {
        let borderX = null;
        try {
            const firstRow = table && table.querySelector('tbody tr');
            if (firstRow) {
                const tds = firstRow.children;
                if (tds && tds.length >= 2) {
                    borderX = Math.round(tds[0].getBoundingClientRect().right - svgRect.left);
                }
            }
        } catch (e) {
            // noop
        }

        const createLine = (x1, y1, x2, y2) => {
            const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', Math.round(x1));
            line.setAttribute('y1', Math.round(y1));
            line.setAttribute('x2', Math.round(x2));
            line.setAttribute('y2', Math.round(y2));
            line.setAttribute('stroke', '#8e9ba7');
            line.setAttribute('stroke-width', '3');
            svg.appendChild(line);
        };

        const calendarBox = document.querySelector('.business-calendar-resource-box');
        const externalBoxes = document.querySelectorAll('.business-calendar-external-box');
        if (!calendarBox || externalBoxes.length === 0) return;

        const calendarRect = calendarBox.getBoundingClientRect();

        externalBoxes.forEach(externalBox => {
            const externalRect = externalBox.getBoundingClientRect();
            const startX = Math.round(calendarRect.right - svgRect.left);
            const startY = Math.round(calendarRect.top + calendarRect.height / 2 - svgRect.top);
            const endX = Math.round(externalRect.left - svgRect.left);
            const endY = Math.round(externalRect.top + externalRect.height / 2 - svgRect.top);
            const trunkX = borderX !== null ? borderX : startX + (endX - startX) / 2;

            createLine(startX, startY, trunkX, startY);
            createLine(trunkX, startY, trunkX, endY);
            createLine(trunkX, endY, endX, endY);
        });
    };

    // 外線番号詳細取得（NumberSettingInfoDiagram.js から流用）
    fetchExternalNumberDetail = async (externalId) => {
        try {
            const reqBody = {
                start: 0,
                length: 10,
                search: { value: '' },
                columns: [],
                order: [{ column: 0, dir: 'desc' }],
                sorted: [{ id: 'id', desc: true }],
                filtered: [
                    { id: 'id', value: externalId }
                ]
            };

            const response = await this.props.ascAxios('post', 'ExternalNumber/board', reqBody);

            if (response.data && response.data.rows && Array.isArray(response.data.rows) && response.data.rows.length > 0) {
                const targetExternal = response.data.rows.find(row => row.id === parseInt(externalId));
                if (targetExternal) {
                    return targetExternal;
                } else {
                    console.warn(this.props.langText.Message.BusinessCalendarNumberSettingInfoDiagram.specifiedDisplayNumberDoesNotFound + externalId);
                    return null;
                }
            } else {
                console.warn(this.props.langText.Message.BusinessCalendarNumberSettingInfoDiagram.specifiedDisplayNumberInfoDoesNotFound);
                return null;
            }
        } catch (error) {
            console.error(this.props.langText.Message.BusinessCalendarNumberSettingInfoDiagram.specifiedDisplayNumberInfoGetError, error);
            throw error;
        }
    }

    // 裏番号取得（NumberSettingInfoDiagram.js から流用）
    getBackNumbers = async (cm61_id) => {
        try {
            if (!cm61_id) return [];

            const result = await this.props.ascAxios(
                "post",
                "ExternalNumber/getBackNumbers",
                { cm61_id }
            );

            const backNumbers = result.data.map(data => {
                return { label: data.back_number, value: data.back_number };
            });

            return backNumbers;
        } catch (error) {
            console.error(this.props.langText.Message.BusinessCalendarNumberSettingInfoDiagram.getBackNumberError, error);
            return [];
        }
    }

    // 外線番号モーダル表示（NumberSettingInfoDiagram.js から流用）
    openExternalNumberModal = async (externalNumberData) => {
        try {
            let setData = {
                inboundTypeSelect: this.getSelectOption('external_inbound_group_type', this.props.langText.SelectOption),
                voice_log_expire_external_number_select: this.getSelectOption("voice_log_expire_external_number", this.props.langText.SelectOption),
                UnconnectedActionSelect: this.getSelectOption('unconnected_action', this.props.langText.SelectOption),
                inboundGroupSelect: [],
                inboundActionSelect: this.getSelectOption('inbound_action', this.props.langText.SelectOption),
                callOverActionSelect: this.getSelectOption('call_over_action', this.props.langText.SelectOption),
                callOverActionControlSelect: this.getSelectOption('call_over_action_control', this.props.langText.SelectOption),
                timeoutPlaybackSelect: this.getSelectOption('timeout_playback', this.props.langText.SelectOption),
                DialRestrictionSelect: this.getSelectOption('dial_restriction', this.props.langText.SelectOption),
                anonymousActionSelect: this.getSelectOption('anonymous_action', this.props.langText.SelectOption)
            };

            if (externalNumberData) {
                setData.external_id = externalNumberData.id || "";
                setData.gateway_id = externalNumberData.cm91_id ? externalNumberData.cm91_id : '';
                setData.gateway_name = externalNumberData.cm91_gateway_infos?.gw_name || '';
                setData.company_id = externalNumberData.cm12_companies?.id || "";
                setData.company_name = externalNumberData.cm12_companies?.company_name || "";

                try {
                    const gwRes = await this.props.ascAxios('post', 'Common/companyBygatewaySelect', { company_id: setData.company_id });
                    setData.gatewaySelect = gwRes.data || [];
                } catch (e) {
                    setData.gatewaySelect = [];
                }

                try {
                    const ltRes = await this.props.ascAxios('post', 'Common/getLineTypes', { parameterId: 'LINE_TYPE' });
                    setData.lineTypeSelect = ltRes.data || [];
                } catch (e) {
                    setData.lineTypeSelect = [];
                }

                setData.display_number = externalNumberData.display_number || "";
                setData.back_number = await this.getBackNumbers(externalNumberData.id);
                setData.channel_number = externalNumberData.channel_number !== "0" ? externalNumberData.channel_number : "";
                setData.memo = externalNumberData.memo || "";
                setData.description = externalNumberData.description || "";

                setData.wait_number = (externalNumberData.wait_number && externalNumberData.wait_number >= 0) || externalNumberData.wait_number === 0 ? externalNumberData.wait_number : "";
                setData.waitCallTimeoutSeconds = externalNumberData.wait_call_timeout_sec || "";

                const departmentInfo = Array.isArray(externalNumberData.ct62_assgin_external_department_infos) &&
                    externalNumberData.ct62_assgin_external_department_infos.length > 0
                    ? externalNumberData.ct62_assgin_external_department_infos[0]
                    : null;
                const departmentInfos = externalNumberData.ct62_assgin_external_department_infos
                    ? externalNumberData.ct62_assgin_external_department_infos.map(row => ({
                        id: row.id,
                        cm61_id: row.cm61_id,
                        cm13_id: row.cm13_departments.id,
                        department_id: row.cm13_departments.id,
                        parent_department_id: row.cm13_departments.parent_department_id,
                        floor_id: row.cm13_departments.floor_id,
                        prefix: row.prefix,
                        full_prefix: row.full_prefix,
                        department_name: row.cm13_departments.department_name,
                        floor_name: row.cm13_departments.floor_name,
                        prefix_duplicationflag: false
                    }))
                    : [];
                setData.departmentInfos = departmentInfos;
                setData.department_id = departmentInfo?.cm13_departments?.id || "";
                setData.department_name = departmentInfo?.cm13_departments?.department_name || "";
                setData.floor_flg = externalNumberData.cm12_companies?.floor_flg === "Y";
                setData.prefix = externalNumberData.sequence_number || "";

                setData.inbound_type = externalNumberData.inbound_group_type || "";
                setData.inboundTypeSelect = this.getSelectOption('external_inbound_group_type', this.props.langText.SelectOption);
                setData.inbound_group_id = externalNumberData.ct29_assign_external_number_extension_group_infos?.cm28_inbound_group_infos
                    ? externalNumberData.ct29_assign_external_number_extension_group_infos.cm28_inbound_group_infos.map(row => row.id)
                    : [];
                setData.inbound_group_name = externalNumberData.ct29_assign_external_number_extension_group_infos?.cm28_inbound_group_infos
                    ? externalNumberData.ct29_assign_external_number_extension_group_infos.cm28_inbound_group_infos.map(row => row.inbound_group_name)
                    : [];

                try {
                    const res = await this.ascAxios("post", "/ExternalNumber/inboundGroupInfoSelect", {
                        department_id: departmentInfos.map(row => row.cm13_id),
                        inbound_type: externalNumberData.inbound_group_type
                    });
                    setData.inbound_group_cm51ids = res.data.cm28_extensions_ids;
                } catch (e) {
                    setData.inbound_group_cm51ids = [];
                }

                setData.sound_id = externalNumberData.cm35_id !== "0" ? externalNumberData.cm35_id : "";
                setData.call_over_sound_id = externalNumberData.cm35_id_call_over_sound || "";
                setData.before_dial_sound_id = externalNumberData.cm35_id_before_dial || "";
                setData.cm35_id_timeout_sound = externalNumberData.cm35_id_timeout_sound || "";
                setData.unconnected_sound_id = externalNumberData.cm35_id_unconnected_sound || "";
                setData.anonymous_sound_id = externalNumberData.cm35_id_anonymous_sound || "";
                setData.wait_call_timeout_sound_id = externalNumberData.cm35_id_wait_call_timeout_sound || "";
                setData.waitCallTimeoutSoundId = externalNumberData.cm35_id_wait_call_timeout_sound || "";

                setData.business_calendar_id = externalNumberData.cm31_business_calendar_infos?.id || "";
                setData.business_calendar_name = externalNumberData.cm31_business_calendar_infos?.business_calendar_name || "";

                setData.callAnalysisFlag = !!(
                    externalNumberData.cc81_setting_call_quality_external_number &&
                    externalNumberData.cc81_setting_call_quality_external_number.parameter_value === 1
                );
                setData.callEvalFlag = !!(
                    externalNumberData.cc82_setting_call_eval_external_number &&
                    externalNumberData.cc82_setting_call_eval_external_number.parameter_value === 1
                );
                setData.fastOptionFlag = !!(
                    externalNumberData.cc81_setting_call_quality_external_number &&
                    externalNumberData.cc81_setting_call_quality_external_number.fast_option === "Y"
                );

                setData.schedule_extension_group_infos = externalNumberData.ct30_assign_external_number_schedule_extension_group_infos || [];
                setData.schedule_wait_call_infos = externalNumberData.ct32_assign_external_number_schedule_wait_call_infos || [];
                setData.schedule_over_wait_call_infos = externalNumberData.ct33_assign_external_number_schedule_over_wait_call_infos || [];
                setData.schedule_dispensing_infos = externalNumberData.ct31_assign_external_number_schedule_dispensing_infos || [];

                setData.acd_flag = externalNumberData.acd_flag === "Y";
                setData.abj_flag = externalNumberData.abj_flag === "Y";
                setData.before_dial_flag = externalNumberData.before_dial_flag === "Y";
                setData.waitCallTimeoutUseFlag = externalNumberData.wait_call_timeout_flag === "Y";
                setData.call_history_delete_flag = externalNumberData.call_history_delete_flag === "Y";
                setData.voice_log_delete_flag = externalNumberData.voice_log_delete_flag === "Y";

                setData.timeout_sec = externalNumberData.timeout_sec || "";
                setData.call_over_action = externalNumberData.call_over_action || "";
                setData.call_over_action_control = externalNumberData.call_over_action_control || "";
                setData.timeout_action = externalNumberData.timeout_action
                    ? externalNumberData.timeout_action
                    : externalNumberData.timeout_playback_flag === "Y"
                        ? "playback"
                        : "unset";
                setData.unconnected_action = externalNumberData.unconnected_action || "";
                setData.anonymous_action = externalNumberData.anonymous_action || 'unset';
                setData.line_type = externalNumberData.line_type || "";
                setData.voice_log_expire_external_number =
                    (externalNumberData.voice_log_expire_external_number || externalNumberData.voice_log_expire_external_number === 0)
                        ? externalNumberData.voice_log_expire_external_number
                        : -1;
                setData.wait_call_timeout_sec = externalNumberData.wait_call_timeout_sec || "";
                setData.call_results = externalNumberData.ct602_assign_call_results
                    ? externalNumberData.ct602_assign_call_results.map(row => row.cm64_id)
                    : [];
                setData.omni_phone_back_ground_color = externalNumberData.omni_phone_back_ground_color
                    ? externalNumberData.omni_phone_back_ground_color
                    : GlobalConst.omni_phone_back_ground_color;
                setData.omni_phone_font_color = externalNumberData.omni_phone_font_color
                    ? externalNumberData.omni_phone_font_color
                    : GlobalConst.omni_phone_font_color;
                setData.before_omni_phone_back_ground_color = externalNumberData.omni_phone_back_ground_color
                    ? externalNumberData.omni_phone_back_ground_color
                    : '';
                setData.before_omni_phone_font_color = externalNumberData.omni_phone_font_color
                    ? externalNumberData.omni_phone_font_color
                    : '';
                setData.dial_restriction_flag = externalNumberData.dial_restriction_flag
                    ? externalNumberData.dial_restriction_flag
                    : "N";
                setData.listAppointInboundFlag = externalNumberData.cm76_autocall_list && externalNumberData.cm76_autocall_list.id;
                setData.list_out_action = externalNumberData.list_out_action ? externalNumberData.list_out_action : '';
                setData.list_name = externalNumberData.cm76_autocall_list ? externalNumberData.cm76_autocall_list.list_name : '';
                setData.list_sound_id = externalNumberData.cm35_id_list_sound ? externalNumberData.cm35_id_list_sound : '';
                setData.dispensing_time = externalNumberData.dispensing_time || "";
                setData.dispensing_count = externalNumberData.dispensing_count || "";
                setData.dispensing_flag = !!(externalNumberData.dispensing_time || externalNumberData.dispensing_count);
            }

            if (externalNumberData.cm12_companies.id) {
                try {
                    let langtext_update = this.props.langText.Body.VoiceListUpdate;
                    let langtext_create = this.props.langText.Body.VoiceListCreate;
                    const resCompanyInfo = await this.ascAxios('post', "/ExternalNumber/getItemsOnCompanySelected", {
                        company_id: externalNumberData.cm12_companies.id,
                        langtext_update,
                        langtext_create
                    });
                    setData.waitNumberSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.listOutSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.beforeDialSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.listSelect = resCompanyInfo.data.cm76_autocall_list;
                    setData.callOverSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.UnconnectedSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.timeoutSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.anonymousSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.waitCallTimeoutSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.callResultsSelect = resCompanyInfo.data.cm64_call_results;
                    setData.call_history_delete_flag_company = resCompanyInfo.data && resCompanyInfo.data.cm12_companies
                        && resCompanyInfo.data.cm12_companies.call_history_delete_flag && resCompanyInfo.data.cm12_companies.call_history_delete_flag === "Y"
                        ? true : false;
                    setData.voice_log_delete_flag_company = resCompanyInfo.data && resCompanyInfo.data.cm12_companies
                        && resCompanyInfo.data.cm12_companies.voice_log_delete_flag && resCompanyInfo.data.cm12_companies.voice_log_delete_flag === "Y"
                        ? true : false;

                    const resCompanyControlParam = await this.ascAxios('post', "/ExternalNumber/getCompanyControlParameter", { company_id: externalNumberData.cm12_companies.id });
                    setData.addingOptionCallAnalysis = resCompanyControlParam.data.paramValueCallAnalysis === "N" ? false : true;
                    setData.addingFastOption = resCompanyControlParam.data.paramValueFastOption === "ascend_whisper" ? true : false;
                    setData.addingOptionACD = resCompanyControlParam.data.paramValueACD === "N" ? false : true;
                    setData.addingOptionCallEval = resCompanyControlParam.data.paramValueCallEval === "N" ? false : true;
                    setData.addingOptionCallResult = resCompanyControlParam.data.paramValueCallResult === "N" ? false : true;
                } catch (e) {
                    // 処理は継続
                }
            }

            setData.showExternalNumberModal = true;
            setData.show = true;
            setData.showConfirm = false;
            setData.modalType = "read";

            this.setState({
                externalNumberModalData: setData,
                showExternalNumberModal: true,
                externalNumberModalLoading: false
            });

        } catch (error) {
            console.error(this.props.langText.Message.BusinessCalendarNumberSettingInfoDiagram.externalNumberModalShowError, error);
            this.setState({
                showExternalNumberModal: false,
                externalNumberModalLoading: false
            });
        }
    }

    getSoundName = (cm35_id) => {
        let { listOutSoundSelect } = this.state.externalNumberModalData;
        let returnData = '';
        listOutSoundSelect.forEach((row) => {
            if (row.value == cm35_id) {
                returnData = row.label;
            }
        });
        return returnData;
    }

    closeExternalNumberModal = () => {
        this.setState({
            showExternalNumberModal: false,
            externalNumberModalLoading: false,
            externalNumberModalData: null
        });
    }

    // 外線番号クリック時のハンドラ
    handleExternalClick = async (event, externalId) => {
        if (event) event.stopPropagation();

        if (!externalId) {
            console.error(this.props.langText.Message.BusinessCalendarNumberSettingInfoDiagram.missingExternalNumberId);
            return;
        }

        this.setState({
            externalNumberModalLoading: true,
            showExternalNumberModal: false
        });

        try {
            const externalNumberData = await this.fetchExternalNumberDetail(externalId);
            if (externalNumberData) {
                await this.openExternalNumberModal(externalNumberData);
            } else {
                this.setState({
                    externalNumberModalLoading: false,
                    showExternalNumberModal: false
                });
            }
        } catch (error) {
            this.setState({
                externalNumberModalLoading: false,
                showExternalNumberModal: false
            });
            this.showErrorObjectMesssage(error);
        }
    };

    // CSV出力処理
    createCsvData = async () => {
        try {
            this.setState({ csvLoading: true });

            const { state, langText } = this.props;
            const { resourceMapData } = state;

            if (!resourceMapData) {
                console.error(langText.Message.BusinessCalendarNumberSettingInfoDiagram.csvEmptyResourceMapDataError);
                alert(langText.Message.BusinessCalendarNumberSettingInfoDiagram.csvNoDataToOutputAlert);
                this.setState({ csvLoading: false });
                return;
            }

            const csvHeader = [
                { label: langText.Body.BusinessCalendarNumberSettingInfoDiagram.csvHeaderBusinessCalendar, key: 'businessCalendar' },
                { label: langText.Body.BusinessCalendarNumberSettingInfoDiagram.csvHeaderDisplayNumber, key: 'displayNumber' },
                { label: langText.Body.BusinessCalendarNumberSettingInfoDiagram.csvHeaderDisplayNumberMemo, key: 'displayNumberMemo' }
            ];

            const externalNumbers = resourceMapData.externalNumbers || [];
            let csvData = [];

            if (externalNumbers.length > 0) {
                externalNumbers.forEach(ext => {
                    csvData.push({
                        businessCalendar: resourceMapData.businessCalendarName || '',
                        displayNumber: ext.display_number || '',
                        displayNumberMemo: ext.memo || ''
                    });
                });
            } else {
                csvData.push({
                    businessCalendar: resourceMapData.businessCalendarName || '',
                    displayNumber: '',
                    displayNumberMemo: ''
                });
            }

            if (csvData.length === 0) {
                console.error(langText.Message.BusinessCalendarNumberSettingInfoDiagram.csvEmptyResourceMapDataError);
                alert(langText.Message.BusinessCalendarNumberSettingInfoDiagram.csvNoDataToOutputAlert);
                this.setState({ csvLoading: false });
                return;
            }

            this.setState({ csvData, csvHeader, csvLoading: false }, () => {
                setTimeout(() => {
                    this.csvLink.current.link.click();
                }, 100);
            });

        } catch (error) {
            console.error(this.props.langText.Message.BusinessCalendarNumberSettingInfoDiagram.csvOutputError, error);
            this.setState({ csvLoading: false });
            alert(this.props.langText.Message.BusinessCalendarNumberSettingInfoDiagram.csvOutputFailedAlert);
        }
    }

    getCsvFileName = () => {
        const { state } = this.props;
        const { resourceMapData } = state;
        const companyName = (resourceMapData && resourceMapData.companyName)
            ? resourceMapData.companyName
            : (this.props.userInfo ? this.props.userInfo.company_name : '');
        const nowTime = moment().utc().add(9, "h").format("YYYYMMDDHHmmss");
        return `${companyName}_${this.props.langText.Body.BusinessCalendarNumberSettingInfoDiagram.csvFileNameFixedWord}_${nowTime}.csv`;
    }

    // テーブルコンテンツのレンダリング
    renderTableContent = ({ resourceMapData, tableClass, connectionLineClass }) => {
        const { langText } = this.props;
        const externalNumbers = resourceMapData.externalNumbers || [];
        const rows = [];

        const header = (
            <thead>
                <tr>
                    <th className="number-setting-info-diagram-th number-setting-info-diagram-th--right-border business-calendar-number-setting-info-diagram-th">
                        {langText.Body.BusinessCalendar}
                    </th>
                    <th className="number-setting-info-diagram-th business-calendar-number-setting-info-diagram-th">
                        {langText.Body.BusinessCalendarNumberSettingInfoDiagram.titleExternal}
                    </th>
                </tr>
            </thead>
        );

        if (externalNumbers.length === 0) {
            // 外線番号0件: 営業カレンダー列のみ、外線番号列は空欄
            rows.push(
                <tr key="no-externals">
                    <td className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border business-calendar-number-setting-info-diagram-td" style={{ padding: "2px 10px" }}>
                        <div className="business-calendar-resource-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px margin-top-6-px">
                            <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faCalendarAlt} size="lg" style={{ flexShrink: 0 }} />
                                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                    <div className="truncate-ellipsis" title={resourceMapData.businessCalendarName}>{resourceMapData.businessCalendarName}</div>
                                </div>
                            </div>
                        </div>
                    </td>
                    <td className="number-setting-info-diagram-td business-calendar-number-setting-info-diagram-td" />
                </tr>
            );
        } else {
            // 外線番号1件以上: 営業カレンダーボックスをrowSpan
            externalNumbers.forEach((ext, idx) => {
                const isFirst = idx === 0;
                rows.push(
                    <tr key={`ext-${idx}`}>
                        {isFirst && (
                            <td
                                rowSpan={externalNumbers.length}
                                className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border business-calendar-number-setting-info-diagram-td"
                                style={{ padding: "2px 10px" }}
                            >
                                <div className="business-calendar-resource-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px margin-top-6-px">
                                    <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                        <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faCalendarAlt} size="lg" style={{ flexShrink: 0 }} />
                                        <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                            <div className="truncate-ellipsis" title={resourceMapData.businessCalendarName}>{resourceMapData.businessCalendarName}</div>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        )}
                        <td
                            className="number-setting-info-diagram-td business-calendar-number-setting-info-diagram-td"
                            style={{ padding: "2px 10px" }}
                        >
                            <div
                                className={`business-calendar-external-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide number-setting-info-clickable minh-50 font-weight-bold text-14-px ${isFirst ? 'margin-top-6-px' : 'margin-top-0'}`}
                                data-external-id={ext.external_number_id}
                                onClick={(event) => this.handleExternalClick(event, ext.external_number_id)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                    <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faFax} size="lg" style={{ flexShrink: 0 }} />
                                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                        <div className="truncate-ellipsis">{ext.display_number}</div>
                                        {ext.memo && (
                                            <div className="text-12-px margin-top-2-px op-90 truncate-ellipsis" title={ext.memo}>
                                                ({ext.memo})
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                );
            });
        }

        rows.push(
            <tr key="spacer-row">
                <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border business-calendar-number-setting-info-spacer-cell" />
                <td className="number-setting-info-spacer-cell business-calendar-number-setting-info-spacer-cell" />
            </tr>
        );

        return (
            <div className="number-setting-info-diagram-wrapper">
                <table className={`${tableClass} number-setting-info-diagram-table`}>
                    {header}
                    <tbody>{rows}</tbody>
                </table>
                <svg className={`${connectionLineClass} number-setting-info-diagram-connection-overlay`} xmlns="http://www.w3.org/2000/svg" />
            </div>
        );
    }

    // 親画面の番号設定構成図モーダルを閉じる
    closeNumberSettingInfoModal = () => {
        try {
            if (this.props && typeof this.props.propSetState === 'function') {
                this.props.propSetState({
                    showResourceMap: false,
                    resourceMapData: null,
                    resourceMapLoading: false
                });
            }
        } catch (e) {
            // noop
        }
    }

    render() {
        const {
            state,
            langText,
            tableClass,
            connectionLineClass
        } = this.props;

        const { showResourceMap, resourceMapLoading, resourceMapData } = state;

        const modalState = {
            show: showResourceMap,
            modalType: "read",
            showConfirm: false
        };

        const wrappedPropSetState = (newState) => {
            if (newState && typeof newState.show !== 'undefined') {
                if (!newState.show) {
                    this.setState({
                        showExternalNumberModal: false,
                        externalNumberModalLoading: false,
                        externalNumberModalData: null
                    });
                    this.closeNumberSettingInfoModal();
                }
            }
        };

        return (
            <div>
                <AscModal
                    state={modalState}
                    propSetState={wrappedPropSetState}
                    langText={{
                        ...langText,
                        Body: {
                            ...(langText?.Body || {}),
                            ReadTitle: langText.Body.BusinessCalendarNumberSettingInfoDiagram.title,
                            Cancel: langText.Body.Close
                        }
                    }}
                    dialogClassName="business-calendar-number-setting-info-diagram-modal non-scroll-bar"
                >
                    <Panel bsStyle="info">
                        <Panel.Heading>
                            <Panel.Title style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontWeight: "bold" }}>{langText.Body.BusinessCalendarNumberSettingInfoDiagram.titleInside}</span>
                                <Button
                                    className="table-button"
                                    bsStyle="default"
                                    bsSize="sm"
                                    onClick={this.createCsvData}
                                    disabled={this.state.csvLoading}
                                >
                                    {this.state.csvLoading
                                        ? langText.Body.BusinessCalendarNumberSettingInfoDiagram.csvLoading
                                        : langText.Body.BusinessCalendarNumberSettingInfoDiagram.csvButton}
                                </Button>
                            </Panel.Title>
                        </Panel.Heading>

                        <Panel.Body style={{ position: "relative" }}>
                            <Row>
                                <Col md={12}>
                                    {resourceMapLoading ? (
                                        <div className="text-center">
                                            <p>{langText.Table.LoadingText}</p>
                                        </div>
                                    ) : resourceMapData ? (
                                        <div>
                                            {this.renderTableContent({ resourceMapData, tableClass, connectionLineClass })}
                                        </div>
                                    ) : (
                                        <div className="text-center">
                                            <p>{langText.Body.BusinessCalendarNumberSettingInfoDiagram.dataReadError}</p>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Panel.Body>
                    </Panel>
                </AscModal>

                {/* 外線番号詳細モーダル */}
                {this.state.showExternalNumberModal && (
                    <ExternalNumberModal
                        state={this.state.externalNumberModalData}
                        propSetState={(newState) => {
                            if (newState && newState.show === false) {
                                this.closeExternalNumberModal();
                            } else {
                                this.setState({
                                    externalNumberModalData: { ...this.state.externalNumberModalData, ...newState }
                                });
                            }
                        }}
                        langText={{
                            ...langText,
                            Body: {
                                ...langText.Body,
                                ReadTitle: langText.Body.BusinessCalendarNumberSettingInfoDiagram.titleExternal,
                                Cancel: langText.Body.Close
                            }
                        }}
                        onClick={this.closeExternalNumberModal}
                        getScopeGreaterEqual={this.props.getScopeGreaterEqual}
                        boardWidth={this.props.boardWidth}
                        getSoundName={this.getSoundName}
                    />
                )}

                {/* CSVダウンロード用の隠しリンク */}
                <CSVLink
                    data={this.state.csvData}
                    headers={this.state.csvHeader}
                    filename={this.getCsvFileName()}
                    className="csv-download-link-format hidden"
                    ref={this.csvLink}
                />
            </div>
        );
    }
}

export default BusinessCalendarNumberSettingInfoDiagram;
